import React, { useState, useEffect } from 'react';
import { Modal, Button, Form } from 'react-bootstrap';
import { useSelector, useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import axios from 'axios';
import './ChangeRoleModal.css';
import { getAllOfficerList } from '../../../../redux/UserReducer/action';
import { getAuthToken } from "../../../../utils/auth";

const ChangeRoleModal = ({ show, onHide, user, currentPage, limit, officerFilters }) => {
    const dispatch = useDispatch();
    const [selectedRole, setSelectedRole] = useState("");
    const [isSaving, setIsSaving] = useState(false);

    const { rolesList } = useSelector((store) => {
        return {
            rolesList: store.UserReducer.roles.rolesList,
        }
    }, []);

    useEffect(() => {
        if (show && user) {
            setSelectedRole("");
        }
    }, [show, user]);

    const handleRoleChange = (e) => {
        setSelectedRole(e.target.value);
    };

    const handleConfirmChange = async () => {
        if (!selectedRole) {
            toast.warning("Please select a new role");
            return;
        }

        let roleId = rolesList?.find((role) => role.short_name === selectedRole)?.role_id;

        // console.log({ selectedRole, user, rolesList, roleId });

        setIsSaving(true);
        try {
            // let tokens = JSON.parse(sessionStorage.getItem("tokens")) || null;
            // let authToken = tokens?.token || null;
            let authToken = getAuthToken();

            const payload = {
                officer_id: user.officer_id,
                // role_code: selectedRole
                role_id: roleId
            };

            const headers = {
                'Authorization': `Bearer ${authToken}`,
                'Content-type': 'application/json'
            };

            // Assuming there is an API to update the role. If not, this path will need adjustment.
            let response = await axios.put(
                `${process.env.REACT_APP_API_BASE_URL}/officer/update-ho-officer-role`, 
                payload,
                { headers }
            );

            toast.success(`${response?.data?.message}`);
            dispatch(getAllOfficerList(currentPage, limit, officerFilters));
            onHide();
        } catch (error) {
            let errorMsg = error?.response?.data?.message || "Failed to update user role";
            console.error("Failed to update user role", error);
            toast.error(errorMsg);
        } finally {
            setIsSaving(false);
        }
    };

    if (!user) return null;

    const currentRole = user?.role?.short_name || user?.role_code;
    
    // Get unique short_names for roles dropdown, including the current role
    const availableRoles = [...new Set(rolesList?.map(role => role?.short_name))]
        .filter(Boolean);

    const isSameRole = selectedRole === currentRole;

    return (
        <Modal show={show} onHide={onHide} centered className="change-role-modal">
            <Modal.Header closeButton>
                <Modal.Title className="text-xl font-bold text-gray-900">Change role</Modal.Title>
            </Modal.Header>
            <Modal.Body className="px-4 py-4">
                <p className="text-sm text-gray-500 mb-4">Update this user's role and permission set</p>
                
                <section className="mb-4 d-flex align-items-center gap-3 user-identity">
                    {/* Avatar Placeholder */}
                    <div className="avatar-placeholder">
                        {user?.first_name?.charAt(0)}{user?.last_name?.charAt(0)}
                    </div>
                    <div>
                        <h2 className="text-lg font-semibold m-0">{[user?.first_name, user?.last_name].join(' ')}</h2>
                        <p className="text-sm text-gray-500 m-0">{user?.email} &middot; {user?.role?.department || "GENRIC"} department</p>
                    </div>
                </section>

                {/* Current Role Display */}
                <div className="mb-4">
                    <Form.Label className="text-sm font-medium text-gray-700">Current role</Form.Label>
                    <Form.Select disabled value={currentRole} className="h-11 bg-gray-50">
                        <option value={currentRole}>{currentRole}</option>
                    </Form.Select>
                </div>

                {/* New Role Dropdown */}
                <div className="mb-4">
                    <Form.Label className="text-sm font-medium text-gray-700">
                        New role <span className="text-danger">*</span>
                    </Form.Label>
                    <Form.Select 
                        value={selectedRole} 
                        onChange={handleRoleChange} 
                        className="h-11 focus-ring-seeds-blue"
                    >
                        <option value="">Select new role</option>
                        {availableRoles.map(role => (
                            <option key={role} value={role}>{role}</option>
                        ))}
                    </Form.Select>
                </div>

                {/* Informational Message */}
                {selectedRole && !isSameRole && (
                    <div className="security-notice bg-gray-50 rounded-lg p-3 d-flex gap-3">
                        <svg className="info-icon text-gray-400 mt-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" width="24" height="24">
                            <path d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2"></path>
                        </svg>
                        <p className="text-sm text-gray-600 m-0 leading-relaxed">
                            Changing this user's role to <span className="font-semibold text-gray-900">{selectedRole}</span> will update their permissions immediately. If this user has an active session, it will be ended — they will need to log in again to access the system with the new role.
                        </p>
                    </div>
                )}
            </Modal.Body>
            <Modal.Footer className="border-t border-gray-100 d-flex justify-content-end gap-2 p-3">
                <Button variant="light" onClick={onHide} className="btn-cancel font-semibold text-gray-700">
                    Cancel
                </Button>
                <Button 
                    variant="primary" 
                    onClick={handleConfirmChange} 
                    className="btn-confirm bg-seeds-blue text-white font-semibold"
                    disabled={isSaving || !selectedRole || isSameRole}
                >
                    {isSaving ? "Saving..." : "Confirm change"}
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

export default ChangeRoleModal;
